/** Поля status localis / профильного статуса в амбулаторном протоколе по специальности. */

export const AMBULATORY_SPECIALTIES = {
  neurology: {
    id: 'neurology',
    title: 'Неврология',
    match: ['невролог', 'neurolog'],
    statusLabel: 'Неврологический статус',
    statusPlaceholder:
      'Сознание, meningeal signs, черепные нервы, двигательная и чувствительная сфера, координация, рефлексы…',
    templateFieldKey: 'protocol.neurologyStatus',
  },
  cardiology: {
    id: 'cardiology',
    title: 'Кардиология',
    match: ['кардиолог', 'кардио', 'cardio', 'экг', 'ecg'],
    statusLabel: 'Кардио-статус',
    statusPlaceholder:
      'ЧСС, ритм, АД, аускультация сердца, шумы, периферические отёки, пульс…',
    templateFieldKey: 'protocol.cardiologyStatus',
  },
  therapy: {
    id: 'therapy',
    title: 'Терапия',
    match: ['терапевт', 'терап'],
    statusLabel: 'Терапевтический статус',
    statusPlaceholder: 'Кожа, лимфоузлы, дыхание, сердце, живот, мочеиспускание…',
    templateFieldKey: 'protocol.therapyStatus',
  },
  uzi: {
    id: 'uzi',
    title: 'УЗИ',
    match: ['узи', 'ultrasound', 'sonograph'],
    statusLabel: 'УЗИ-картина',
    statusPlaceholder:
      'Эхогенность, контуры, размеры, кровоток, заключение по зоне исследования…',
    templateFieldKey: 'protocol.uziStatus',
  },
  endoscopy: {
    id: 'endoscopy',
    title: 'Эндоскопия',
    match: ['эгдс', 'egds', 'колоноскоп', 'colonoscop'],
    statusLabel: 'Эндоскопическая картина',
    statusPlaceholder:
      'Пищевод, желудок, ДПК / толстая кишка: слизистая, складки, эрозии, полипы, заключение…',
    templateFieldKey: 'protocol.endoscopyStatus',
  },
  dental: {
    id: 'dental',
    title: 'Стоматология',
    match: ['стомат', 'зуб', 'dental'],
    statusLabel: 'Стоматологический статус',
    statusPlaceholder: 'Состояние полости рта, зубов, дёсен, прикус, перкуссия…',
    templateFieldKey: 'protocol.dentalStatus',
  },
  surgery: {
    id: 'surgery',
    title: 'Хирургия',
    match: ['хирург', 'surgeon'],
    statusLabel: 'Хирургический статус',
    statusPlaceholder:
      'Область осмотра, рубцы, послеоперационные изменения, пальпация, перистальтика…',
    templateFieldKey: 'protocol.surgeryStatus',
  },
  orthopedics: {
    id: 'orthopedics',
    title: 'Ортопедия',
    match: ['ортопед', 'травматолог', 'orthop'],
    statusLabel: 'Ортопедический статус',
    statusPlaceholder: 'Оси конечностей, объём движений, деформации, болезненность, хромота…',
    templateFieldKey: 'protocol.orthopedicsStatus',
  },
  ent: {
    id: 'ent',
    title: 'ЛОР',
    match: ['лор', 'отоларинг', 'otorhin'],
    statusLabel: 'ЛОР-статус',
    statusPlaceholder: 'Нос, глотка, гортань, уши, шёпотная речь, перкуссия пазух…',
    templateFieldKey: 'protocol.entStatus',
  },
  gynecology: {
    id: 'gynecology',
    title: 'Гинекология',
    match: ['гинеколог', 'gynec'],
    statusLabel: 'Гинекологический статус',
    statusPlaceholder:
      'Наружные половые органы, влагалище, шейка матки, придатки (по показаниям)…',
    templateFieldKey: 'protocol.gynecologyStatus',
  },
  urology: {
    id: 'urology',
    title: 'Урология',
    match: ['уролог', 'урологи', 'урология', 'urolog'],
    statusLabel: 'Урологический статус',
    statusPlaceholder:
      'Почки, мочевой пузырь, простата, наружные половые органы, мочеиспускание, болезненность…',
    templateFieldKey: 'protocol.urologyStatus',
  },
  phlebology: {
    id: 'phlebology',
    title: 'Флебология',
    match: ['флеболог', 'флеб', 'варикоз', 'вен', 'эвлк', 'evlk', 'склеротерап'],
    statusLabel: 'Флебологический / венозный статус',
    statusPlaceholder:
      'Локальный статус нижних конечностей (норма, ретикулярные, подкожные, после ЭВЛК/склеротерапии). УЗИ вен (ДСВ), CEAP…',
    templateFieldKey: 'protocol.phlebologyStatus',
  },
  general: {
    id: 'general',
    title: 'Общий профиль',
    match: [],
    statusLabel: 'Локальный статус',
    statusPlaceholder: 'Осмотр по локализации жалоб…',
    templateFieldKey: 'protocol.protocolLocalStatus',
  },
};

export const DEFAULT_AMBULATORY_SPECIALTY_ID = 'general';

function matchAmbulatorySpecialtyFromText(text) {
  const name = String(text || '')
    .trim()
    .toLowerCase();
  if (!name) return null;

  for (const specialty of Object.values(AMBULATORY_SPECIALTIES)) {
    if (specialty.id === DEFAULT_AMBULATORY_SPECIALTY_ID) continue;
    if (specialty.match.some((keyword) => name.includes(String(keyword).toLowerCase()))) {
      return specialty;
    }
  }

  return null;
}

/** Приоритет: врач → услуга → сохранённая специальность → общий профиль. */
export function resolveAmbulatorySpecialty(serviceName, savedSpecialty = null, doctorName = null) {
  const fromDoctor = matchAmbulatorySpecialtyFromText(doctorName);
  if (fromDoctor) return fromDoctor;

  const fromService = matchAmbulatorySpecialtyFromText(serviceName);
  if (fromService) return fromService;

  if (savedSpecialty && AMBULATORY_SPECIALTIES[savedSpecialty]) {
    return AMBULATORY_SPECIALTIES[savedSpecialty];
  }

  return AMBULATORY_SPECIALTIES[DEFAULT_AMBULATORY_SPECIALTY_ID];
}

export function ambulatorySpecialtyStatusField(serviceName, savedSpecialty = null, doctorName = null) {
  const specialty = resolveAmbulatorySpecialty(serviceName, savedSpecialty, doctorName);

  return {
    key: 'protocolSpecialtyStatus',
    fieldKey: specialty.templateFieldKey,
    label: specialty.statusLabel,
    placeholder: specialty.statusPlaceholder,
    minH: '72px',
    specialtyId: specialty.id,
    specialtyTitle: specialty.title,
  };
}

export function ambulatorySpecialtyStatusLabel(serviceName, savedSpecialty = null, doctorName = null) {
  return resolveAmbulatorySpecialty(serviceName, savedSpecialty, doctorName).statusLabel;
}
